import React, { useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LuX } from 'react-icons/lu';
import { cx } from '../../utils/cx.js';
import { useDialog } from '../../hooks/useDialog.js';
import IconChip from './IconChip';
import IconButton from './IconButton';

const SIZES = {
  s: 'max-w-md',
  sm: 'max-w-md',
  m: 'max-w-2xl',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  l: 'max-w-[95vw]',
  xl: 'max-w-[98vw]',
};

/**
 * Dialog rendered in a portal. Header (icon, title, subtitle, close), scrollable body,
 * optional sticky footer for actions. `fullHeight` gives the body the whole viewport
 * height (JSON viewers). `dismissible={false}` blocks Escape / overlay while busy.
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle = null,
  icon = null,
  iconTone = 'brand',
  children,
  footer = null,
  size = 'md',
  fullHeight = false,
  dismissible = true,
  initialFocusRef = null,
  bodyClassName = '',
}) => {
  const panelRef = useRef(null);
  const titleId = useId();
  useDialog({ isOpen, onClose, panelRef, initialFocusRef, dismissible });

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/50 p-3 backdrop-blur-[2px] sm:p-6"
      onMouseDown={(e) => { if (dismissible && e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cx(
          'flex w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-2xl outline-none dark:border-gray-800 dark:bg-night-800',
          SIZES[size] || SIZES.md,
          fullHeight ? 'h-[92vh]' : 'max-h-[90vh]',
        )}
      >
        {(title || icon) && (
          <div className="flex flex-none items-start gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-900">
            {icon && <IconChip icon={icon} tone={iconTone} />}
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
              {subtitle && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
            </div>
            <IconButton icon={LuX} label="Fermer" onClick={onClose} disabled={!dismissible} className="-mr-1.5 -mt-0.5" />
          </div>
        )}

        <div className={cx(fullHeight ? 'flex min-h-0 flex-1 flex-col' : 'min-h-0 flex-1 overflow-y-auto px-5 py-4', bodyClassName)}>
          {children}
        </div>

        {footer && (
          <div className="flex flex-none flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-5 py-3 dark:border-gray-900 dark:bg-night-850">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
